import { useMemo } from "react";
import type { DeviceStatus } from "@safers/api";
import { Popover, PopoverAnchor, PopoverContent } from "@safers/ui";
import { SEVERITY_LABELS, severityTextColor } from "./severity";
import "./modelMarkers.css";
import {
  DEVICE_TYPES,
  markerColor,
  OFFLINE_COLOR,
  type ModelDeviceFilter,
  type ModelDevicePoint,
  type ModelDeviceType,
} from "./deviceTypes";
export interface ModelDeviceSelection {
  id: string;
  anchor: HTMLElement;
  boundary: HTMLElement;
  side: "left" | "right";
}
/** 핀이 영역 왼쪽 절반이면 오른쪽에, 아니면 왼쪽에 팝업을 연다 — 2D 지도와 3D 뷰어 공통. */
export function popoverSide(
  anchor: HTMLElement,
  boundary: HTMLElement
): ModelDeviceSelection["side"] {
  const anchorBox = anchor.getBoundingClientRect();
  const boundaryBox = boundary.getBoundingClientRect();
  return anchorBox.left + anchorBox.width / 2 < boundaryBox.left + boundaryBox.width / 2
    ? "right"
    : "left";
}
function DeviceIcon({ type }: { type: ModelDeviceType }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={DEVICE_TYPES[type].path} />
    </svg>
  );
}
const STATUS_TAG_TEXT: Record<Exclude<DeviceStatus, "ACTIVE">, string> = {
  DELAYED: "지연",
  OFFLINE: "끊김",
};
/**
 * 통신 상태 태그 — 이름 옆에 작은 테두리 글자로 붙인다. 지연·끊김이 같은 모양이고
 * 위험도(점 + 색 글자)와 섞이지 않는다. 통신 중이면 그리지 않는다. 장비 목록과 팝업이 같이 쓴다.
 */
export function DeviceStatusBadge({ status }: { status: DeviceStatus }) {
  if (status === "ACTIVE") return null;
  return (
    <span className="flex-none rounded border border-neutral-300 px-1 text-[0.625rem] leading-4 font-medium text-neutral-500">
      {STATUS_TAG_TEXT[status]}
    </span>
  );
}
const lastSeenText = (device: ModelDevicePoint) =>
  device.lastSeenTime
    ? `마지막 수신 ${device.lastSeenTime} · ${device.lastSeenAgo}`
    : "수신 기록 없음";
function OfflineNotice({ device }: { device: ModelDevicePoint }) {
  return (
    <div className="mt-3 rounded-lg bg-neutral-50 p-2.5">
      <p className="text-[0.8125rem] font-semibold text-neutral-700">
        통신 끊김{device.lastSeenAgo && ` · ${device.lastSeenAgo}`}
      </p>
      <p className="mt-0.5 text-[0.6875rem] text-neutral-500">
        {device.lastSeenTime ? `마지막 수신 ${device.lastSeenTime}` : "수신 기록 없음"}
      </p>
    </div>
  );
}
const HEADLINE_FIELD = "apparent_temperature";
const HIDDEN_FIELDS = new Set(["lat", "lon", "distance"]);
function DeviceMeasurements({ device }: { device: ModelDevicePoint }) {
  const lastSeen = lastSeenText(device);
  const measurements = device.measurements?.filter((m) => !HIDDEN_FIELDS.has(m.field)) ?? [];
  if (!measurements.length) {
    return (
      <>
        <p className="text-xs text-neutral-500">측정값 없음</p>
        <p className="mt-3 text-[0.6875rem] text-neutral-500">{lastSeen}</p>
      </>
    );
  }
  const headline = measurements.find((m) => m.field === HEADLINE_FIELD);
  const rest = measurements.filter((m) => m !== headline);
  return (
    <>
      {headline && (
        <dl className={rest.length ? "mb-3" : undefined}>
          <dt className="text-[0.6875rem] leading-tight text-neutral-500">
            {headline.displayName || headline.field}
          </dt>
          <dd className="text-xl font-bold leading-tight tabular-nums text-neutral-900">
            {headline.value}
            {headline.unit && (
              <span className="ml-0.5 text-xs font-medium text-neutral-500">{headline.unit}</span>
            )}
          </dd>
        </dl>
      )}
      {rest.length > 0 && (
        <dl className="grid grid-cols-[repeat(auto-fill,minmax(4.75rem,1fr))] gap-x-3 gap-y-2">
          {rest.map((m) => (
            <div key={m.field} className="min-w-0">
              <dt className="truncate text-[0.6875rem] leading-tight text-neutral-500">
                {m.displayName || m.field}
              </dt>
              <dd className="truncate text-[0.8125rem] font-semibold leading-tight tabular-nums text-neutral-900">
                {m.value}
                {m.unit && (
                  <span className="ml-0.5 text-[0.6875rem] font-medium text-neutral-500">
                    {m.unit}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
      <p className="mt-3 text-[0.6875rem] text-neutral-500">{lastSeen}</p>
    </>
  );
}
export function ModelDevicePopover({
  selection,
  selected,
  onClose,
}: {
  selection: ModelDeviceSelection | null;
  selected?: ModelDevicePoint;
  onClose: () => void;
}) {
  const popupAnchor = useMemo(
    () => (selection ? { current: selection.anchor } : undefined),
    [selection]
  );
  return (
    <Popover
      open={!!selection && !!selected}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <PopoverAnchor virtualRef={popupAnchor} />
      <PopoverContent
        side={selection?.side ?? "right"}
        align="center"
        sideOffset={10}
        collisionBoundary={selection?.boundary}
        collisionPadding={12}
        aria-label="선택한 장비"
        className="model-device-popover"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (selection?.anchor.isConnected) selection.anchor.focus();
        }}
      >
        {selected && (
          <>
            <div className="flex items-start gap-2.5">
              <span
                className="model-device-popover__icon"
                style={{
                  color:
                    selected.status === "OFFLINE"
                      ? OFFLINE_COLOR
                      : severityTextColor(selected.severity),
                  background: `color-mix(in srgb, ${markerColor(selected)} 10%, white)`,
                }}
                aria-hidden="true"
              >
                <DeviceIcon type={selected.type} />
              </span>
              <div className="min-w-0 flex-1">
                <strong className="block break-words text-sm">
                  {selected.name || selected.deviceId}
                </strong>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-neutral-500">
                    {DEVICE_TYPES[selected.type].label}
                  </span>
                  <DeviceStatusBadge status={selected.status} />
                </div>
                {selected.status !== "OFFLINE" && (
                  <div className="mt-1 flex items-center gap-1.5 text-xs">
                    <span
                      className="flex items-center gap-1.5 font-medium"
                      style={{ color: severityTextColor(selected.severity) }}
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: markerColor(selected) }}
                        aria-hidden="true"
                      />
                      {selected.statusLabel ?? SEVERITY_LABELS[selected.severity]}
                    </span>
                  </div>
                )}
              </div>
              <button
                type="button"
                aria-label="장비 정보 닫기"
                className="model-device-popover__close"
                onClick={() => onClose()}
              >
                ×
              </button>
            </div>
            {selected.status === "OFFLINE" ? (
              <OfflineNotice device={selected} />
            ) : (
              selected.measurements && (
                <div className="mt-3 border-t border-neutral-100 pt-3">
                  <DeviceMeasurements device={selected} />
                </div>
              )
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
export function ModelDeviceFilters({
  devices,
  filter,
  onChange,
  position = "top-3 left-3",
}: {
  devices: ModelDevicePoint[];
  filter: ModelDeviceFilter;
  onChange: (filter: ModelDeviceFilter) => void;
  position?: string;
}) {
  return (
    <div
      className={`absolute ${position} z-[500] max-w-[calc(100%-1.5rem)]`}
      role="group"
      aria-label="장비 표시 설정"
    >
      <div className="flex flex-wrap gap-1">
        {(Object.keys(DEVICE_TYPES) as ModelDeviceType[]).map((type) => (
          <button
            key={type}
            type="button"
            aria-pressed={filter[type]}
            onClick={() => onChange({ ...filter, [type]: !filter[type] })}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium ${filter[type] ? "border-neutral-800 bg-neutral-800 text-white" : "border-neutral-200 bg-white text-neutral-500"}`}
          >
            <DeviceIcon type={type} />
            {DEVICE_TYPES[type].label} {devices.filter((device) => device.type === type).length}
          </button>
        ))}
      </div>
    </div>
  );
}
